/**
 * Vendor returns-policy update service.
 *
 * Lives in its own file so we can ship buyer-side returns without
 * touching `shopVendors.ts` (which is being concurrently extended by
 * the KYC / vendor-experience worker). Only the two policy fields are
 * writable from this surface; everything else goes through
 * `updateShopVendor`.
 */

import { supabase } from '../client';
import type { ShopVendor } from './shopTypes';

export interface UpdateVendorReturnsPolicyInput {
  vendorId: string;
  returnWindowDays?: number;
  acceptsReturns?: boolean;
}

export async function updateVendorReturnsPolicy(
  input: UpdateVendorReturnsPolicyInput,
): Promise<ShopVendor> {
  const patch: Record<string, unknown> = {};
  if (input.returnWindowDays !== undefined) {
    if (!Number.isInteger(input.returnWindowDays) || input.returnWindowDays < 0) {
      throw new Error('return_window_days must be a non-negative integer');
    }
    patch.return_window_days = input.returnWindowDays;
  }
  if (input.acceptsReturns !== undefined) {
    patch.accepts_returns = !!input.acceptsReturns;
  }
  const { data, error } = await (supabase() as any)
    .schema('shop')
    .from('vendors')
    .update(patch)
    .eq('id', input.vendorId)
    .select('*')
    .single();
  if (error) throw error;
  return data as ShopVendor;
}
